import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Popover } from "antd";
import Icon from "@/components/ui/Icon";
import Badge from "@/components/ui/Badge";
import AccountMenu from "@/components/layout/AccountMenu";
import { useAuth } from "@/contexts/AuthContext";
import { useAccount } from "@/hooks/useAccount";
import { useError } from "@/contexts/ErrorContext";
import { useUserRoles } from "@/hooks/useUserRoles";
import { NAV_MODULES, NavModule, canSee } from "@/routes/navigation";
import { PATHS } from "@/routes/paths";
import { initials } from "@/utils/format";
import styles from "./styles.module.scss";

interface TopbarProps {
  activeModule?: NavModule["key"];
  onOpenMobileNav: () => void;
}

// Menu superior: logo, módulos e botão da conta.
const Topbar: React.FC<TopbarProps> = ({ activeModule, onOpenMobileNav }) => {
  const { user, account: sessionAccount } = useAuth();
  const { account: accountData } = useAccount();
  const account = accountData ?? sessionAccount;
  const { roles } = useUserRoles();
  const { has403Error } = useError();
  const [menuOpen, setMenuOpen] = useState(false);

  const modules = NAV_MODULES.filter((m) => !m.hidden && canSee(m.roles, roles));

  return (
    <header className={styles.topbar}>
      <button
        type="button"
        className={styles.mobileToggle}
        onClick={onOpenMobileNav}
        aria-label="Abrir menu"
      >
        <Icon name="menu" size={20} />
      </button>

      <Link to={PATHS.home} className={styles.logo} aria-label="Yuha — início">
        <img src="/images/logo.webp" alt="Yuha!" />
      </Link>

      <nav aria-label="Módulos" className={styles.nav}>
        {modules.map((module) => {
          const disabled = module.comingSoon || (has403Error && module.key !== "conta");
          const content = (
            <>
              {module.label}
              {module.comingSoon && (
                <Badge tone="subtle" compact>
                  Em breve
                </Badge>
              )}
            </>
          );

          if (disabled) {
            return (
              <span key={module.key} className={`${styles.module} ${styles.disabled}`} aria-disabled="true">
                {content}
              </span>
            );
          }

          return (
            <Link
              key={module.key}
              to={module.pages.find((p) => canSee(p.roles, roles))?.path ?? PATHS.home}
              className={`${styles.module} ${module.key === activeModule ? styles.active : ""}`}
              aria-current={module.key === activeModule ? "page" : undefined}
            >
              {content}
            </Link>
          );
        })}
      </nav>

      <Popover
        trigger="click"
        open={menuOpen}
        onOpenChange={setMenuOpen}
        placement="bottomRight"
        arrow={false}
        overlayInnerStyle={{ padding: 0, background: "transparent", boxShadow: "none" }}
        content={<AccountMenu onNavigate={() => setMenuOpen(false)} />}
      >
        <button
          type="button"
          className={styles.account}
          aria-label="Menu da conta e do usuário"
          aria-expanded={menuOpen}
        >
          <span className={styles.avatar}>{initials(account?.name ?? user?.name)}</span>
          <span className={styles.accountText}>
            <span className={styles.accountName}>{account?.name ?? "Minha conta"}</span>
            <span className={styles.userName}>{user?.name}</span>
          </span>
          <Icon name="chevronDown" size={16} />
        </button>
      </Popover>
    </header>
  );
};

export default Topbar;
